import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ChevronDown,
  Plus,
  Star,
  Trash2,
  Search,
  Users,
  FileText,
  Upload,
} from "lucide-react";
import { cn } from "@slumbr/ui";
import { useNotesStore } from "../stores/notes-store";
import type { Note } from "../types";
import { getDescendantIds } from "../stores/notes-store";
import { TwemojiText } from "@/apps/messenger/components/twemoji-text";
import { markdownToContent } from "../utils/import-markdown";

interface TreeItemProps {
  note: Note;
  level: number;
  onSelect: (noteId: string) => void;
  activeNoteId: string | null;
  draggedNoteId: string | null;
  dropTarget: { id: string; position: "before" | "inside" | "after" } | null;
  onDragStart: (noteId: string) => void;
  onDragEnd: () => void;
  onPreviewDrop: (targetId: string, position: "before" | "inside" | "after") => void;
  onDropNote: (targetId: string, position: "before" | "inside" | "after") => void;
  isFavoriteList?: boolean;
  onDragLeave?: () => void;
}

function TreeItem({
  note,
  level,
  onSelect,
  activeNoteId,
  draggedNoteId,
  dropTarget,
  onDragStart,
  onDragEnd,
  onPreviewDrop,
  onDropNote,
  isFavoriteList = false,
  onDragLeave,
}: TreeItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const notes = useNotesStore((s) => s.notes);
  const deleteNoteRemote = useNotesStore((s) => s.deleteNoteRemote);
  const childNotes = useMemo(
    () =>
      notes
        // In-note pages are a separate per-note feature — never list them in
        // the workspace sidebar (they only appear in a note's page rail).
        .filter((n) => n.parentId === note.id && !n.isDeleted && !n.isPage)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id)),
    [notes, note.id]
  );
  const hasChildren = childNotes.length > 0;
  const isDragging = draggedNoteId === note.id;
  const isDropInside = !isFavoriteList && dropTarget?.id === note.id && dropTarget.position === "inside";
  const isDropBefore = !isFavoriteList && dropTarget?.id === note.id && dropTarget.position === "before";
  const isDropAfter = !isFavoriteList && dropTarget?.id === note.id && dropTarget.position === "after";

  useEffect(() => {
    if (!contextMenu) return;

    const handleMouseDown = (event: MouseEvent) => {
      if (contextMenuRef.current?.contains(event.target as Node)) return;
      setContextMenu(null);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setContextMenu(null);
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [contextMenu]);

  const handleDelete = () => {
    setContextMenu(null);
    void deleteNoteRemote(note.id)
      .then(() => {
        if (activeNoteId === note.id) navigate("/notes");
      })
      .catch(() => {});
  };

  return (
    <div className="relative">
      <div
        role="button"
        tabIndex={0}
        draggable
        onClick={() => onSelect(note.id)}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setContextMenu({ x: e.clientX, y: e.clientY });
        }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onSelect(note.id); }}
        onDragStart={() => onDragStart(note.id)}
        onDragEnd={onDragEnd}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isFavoriteList || isDragging || !draggedNoteId) return;

          const invalidTargets = new Set(getDescendantIds(draggedNoteId, notes));
          if (invalidTargets.has(note.id)) return;

          const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
          const offsetY = e.clientY - rect.top;
          const ratio = offsetY / rect.height;
          const position = ratio < 0.25 ? "before" : ratio > 0.75 ? "after" : "inside";
          onPreviewDrop(note.id, position);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isFavoriteList || isDragging || !draggedNoteId) return;

          const invalidTargets = new Set(getDescendantIds(draggedNoteId, notes));
          if (invalidTargets.has(note.id)) return;

          const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
          const offsetY = e.clientY - rect.top;
          const ratio = offsetY / rect.height;
          const position = ratio < 0.25 ? "before" : ratio > 0.75 ? "after" : "inside";
          onDropNote(note.id, position);
          onDragEnd();
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDragLeave?.();
        }}
        className={cn(
          "flex w-full cursor-pointer items-center gap-1.5 rounded-xl px-2 py-1.5 text-left text-sm transition relative",
          activeNoteId === note.id
            ? "bg-[#ff477e]/15 text-[#ff8fac]"
            : "text-[#d7dbdc] hover:bg-white/[0.04]",
          isDropInside && "ring-1 ring-[#ff6b8f]/60 bg-[#ff477e]/10",
          isDragging && "opacity-50"
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
      >
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="shrink-0"
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        )}
        <span className="text-sm">
          <TwemojiText text={note.icon || "📄"} />
        </span>
        <span className="truncate">{note.title || "Untitled"}</span>
        {note.isFavorite && (
          <Star className="ml-auto h-3 w-3 fill-amber-400 text-amber-400" />
        )}

        {isDropBefore && (
          <span className="absolute top-0 left-2 right-2 h-0.5 rounded-full bg-[#ff6b8f] pointer-events-none z-10" />
        )}
        {isDropAfter && (
          <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-[#ff6b8f] pointer-events-none z-10" />
        )}
      </div>
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="fixed z-50 w-40 overflow-hidden rounded-2xl border border-white/[0.08] bg-[#101214] py-1 shadow-2xl shadow-black/60"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          role="menu"
        >
          <button
            onClick={handleDelete}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-bold text-red-300 transition hover:bg-red-500/10"
            role="menuitem"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      )}
      {isExpanded && hasChildren && (
        <div>
          {childNotes.map((child) => (
            <TreeItem
              key={child.id}
              note={child}
              level={level + 1}
              onSelect={onSelect}
              activeNoteId={activeNoteId}
              draggedNoteId={draggedNoteId}
              dropTarget={dropTarget}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onPreviewDrop={onPreviewDrop}
              onDropNote={onDropNote}
              isFavoriteList={isFavoriteList}
              onDragLeave={onDragLeave}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface WorkspaceTreeProps {
  onSelectNote: (noteId: string) => void;
  onNewNote: () => void;
  onOpenTrash?: () => void;
}

export function WorkspaceTree({ onSelectNote, onNewNote, onOpenTrash }: WorkspaceTreeProps) {
  const notes = useNotesStore((s) => s.notes);
  const sharedNotes = useNotesStore((s) => s.sharedNotes);
  const activeNoteId = useNotesStore((s) => s.activeNoteId);
  const reorderNote = useNotesStore((s) => s.reorderNote);
  const createNoteRemote = useNotesStore((s) => s.createNoteRemote);
  const updateNoteRemote = useNotesStore((s) => s.updateNoteRemote);
  const searchQuery = useNotesStore((s) => s.searchQuery);
  const setSearchQuery = useNotesStore((s) => s.setSearchQuery);
  const [draggedNoteId, setDraggedNoteId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; position: "before" | "inside" | "after" } | null>(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  // Import Markdown / plain-text / Notion-exported .md files as new notes.
  const handleImportFiles = async (files: FileList) => {
    setImporting(true);
    let lastId: string | null = null;
    try {
      for (const file of Array.from(files)) {
        const text = await file.text();
        const h1 = /^#\s+(.+)$/m.exec(text);
        const title =
          (h1?.[1] ?? file.name.replace(/\.(md|markdown|txt|html?)$/i, "")).trim() ||
          "Imported note";
        const id = await createNoteRemote({ title, parentId: null });
        await updateNoteRemote(id, { content: markdownToContent(text) });
        lastId = id;
      }
      if (lastId) onSelectNote(lastId);
    } catch {
      // Best-effort import; surfaced via the store's error state if it fails.
    } finally {
      setImporting(false);
    }
  };

  const rootNotes = useMemo(
    () => notes.filter((n) => !n.parentId && !n.isDeleted && !n.isPage).sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id)),
    [notes]
  );
  const favoriteNotes = useMemo(
    () => notes.filter((n) => n.isFavorite && !n.isDeleted && !n.isPage).sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id)),
    [notes]
  );

  const searchTerm = searchQuery.trim().toLowerCase();
  const isSearching = searchTerm.length > 0;
  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    return notes
      .filter((note) => !note.isDeleted && !note.isPage)
      .filter((note) => {
        if (note.title.toLowerCase().includes(searchTerm)) return true;
        if (note.tags.some((tag) => tag.toLowerCase().includes(searchTerm))) return true;
        return false;
      })
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }, [isSearching, searchTerm, notes]);

  const handleDropNote = (targetId: string, position: "before" | "inside" | "after") => {
    if (!draggedNoteId || draggedNoteId === targetId) {
      return;
    }

    const invalidTargets = new Set(getDescendantIds(draggedNoteId, notes));
    if (invalidTargets.has(targetId)) {
      setDropTarget(null);
      return;
    }

    const targetNote = notes.find((note) => note.id === targetId);
    if (!targetNote) return;

    if (position === "inside") {
      const childNotes = notes
        .filter((note) => note.parentId === targetId && !note.isDeleted && !note.isPage)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id));
      reorderNote(draggedNoteId, targetId, childNotes.length);
    } else {
      const targetParentId = targetNote.parentId ?? null;
      const siblings = notes
        .filter((note) => (note.parentId ?? null) === targetParentId && !note.isDeleted && !note.isPage)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id));
      const targetIndex = siblings.findIndex((note) => note.id === targetId);
      const insertIndex = position === "before" ? targetIndex : targetIndex + 1;
      reorderNote(draggedNoteId, targetParentId, insertIndex);
    }

    setDropTarget({ id: targetId, position });
  };

  const previewDropTarget = (targetId: string, position: "before" | "inside" | "after") => {
    if (!draggedNoteId || draggedNoteId === targetId) {
      setDropTarget(null);
      return;
    }

    const invalidTargets = new Set(getDescendantIds(draggedNoteId, notes));
    if (invalidTargets.has(targetId)) {
      setDropTarget(null);
      return;
    }

    setDropTarget({ id: targetId, position });
  };

  const finishDrag = () => {
    setDraggedNoteId(null);
    setDropTarget(null);
  };

  return (
    <div
      className="flex h-full flex-col"
      onDragLeave={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const { clientX, clientY } = e;
        if (
          clientX < rect.left ||
          clientX >= rect.right ||
          clientY < rect.top ||
          clientY >= rect.bottom
        ) {
          setDropTarget(null);
        }
      }}
    >
      <div className="px-3 pt-4 pb-2">
        <div className="flex items-center gap-2 rounded-2xl border border-white/[0.06] bg-white/[0.02] px-3 py-2 transition focus-within:border-[#ff6b8f]/40 focus-within:bg-white/[0.04]">
          <Search className="h-3.5 w-3.5 text-[#71767b]" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search notes…"
            className="min-w-0 flex-1 bg-transparent text-sm text-[#e7e9ea] outline-none placeholder:text-[#536471]"
          />
        </div>
      </div>

      <div className="flex gap-2 px-3 pb-3">
        <button
          onClick={onNewNote}
          className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#c83050] px-3 py-2 text-[12px] font-black uppercase tracking-wide text-white transition hover:bg-[#d63b5d]"
        >
          <Plus className="h-3.5 w-3.5" />
          New Note
        </button>
        <button
          onClick={() => importInputRef.current?.click()}
          disabled={importing}
          title="Import Markdown / Notion export"
          aria-label="Import notes"
          className="flex items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.02] px-3 py-2 text-[#71767b] transition hover:border-[#ff6b8f]/40 hover:text-[#e7e9ea] disabled:opacity-50"
        >
          <Upload className="h-3.5 w-3.5" />
        </button>
        <input
          ref={importInputRef}
          type="file"
          accept=".md,.markdown,.txt,.html,.htm,text/markdown,text/plain"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) void handleImportFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {isSearching ? (
        <div className="flex-1 overflow-auto px-3 pb-3">
          <p className="mb-1 px-2 text-[10px] font-black uppercase tracking-[0.22em] text-[#536471]">
            Results — {searchResults.length}
          </p>
          {searchResults.length === 0 ? (
            <p className="rounded-xl border border-dashed border-white/[0.06] px-3 py-4 text-center text-[12px] text-[#71767b]">
              No matches.
            </p>
          ) : (
            searchResults.map((note) => (
              <button
                key={note.id}
                type="button"
                onClick={() => onSelectNote(note.id)}
                className={cn(
                  "flex w-full items-start gap-2 rounded-xl px-2 py-2 text-left transition",
                  activeNoteId === note.id
                    ? "bg-[#ff477e]/15 text-[#ff8fac]"
                    : "text-[#d7dbdc] hover:bg-white/[0.04]"
                )}
              >
                <span className="mt-0.5 shrink-0 text-base leading-none">
                  <TwemojiText text={note.icon || "📄"} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold">{note.title || "Untitled"}</span>
                  {note.tags.length > 0 && (
                    <span className="mt-1 flex flex-wrap gap-1">
                      {note.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-white/[0.04] px-1.5 py-0.5 text-[10px] font-bold text-[#71767b]"
                        >
                          #{tag}
                        </span>
                      ))}
                    </span>
                  )}
                </span>
              </button>
            ))
          )}
        </div>
      ) : null}

      {!isSearching && favoriteNotes.length > 0 && (
        <div className="px-3 pb-2">
          <p className="mb-1 flex items-center gap-1 px-2 text-[10px] font-black uppercase tracking-[0.22em] text-[#536471]">
            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
            Favorites
          </p>
          {favoriteNotes.map((note) => (
            <TreeItem
              key={note.id}
              note={note}
              level={0}
              onSelect={onSelectNote}
              activeNoteId={activeNoteId}
              draggedNoteId={draggedNoteId}
              dropTarget={dropTarget}
              onDragStart={setDraggedNoteId}
              onDragEnd={finishDrag}
              onPreviewDrop={previewDropTarget}
              onDropNote={handleDropNote}
              isFavoriteList={true}
            />
          ))}
        </div>
      )}

      <div className={cn("flex-1 overflow-auto px-3", isSearching && "hidden")}>
        <p className="mb-1 px-2 text-[10px] font-black uppercase tracking-[0.22em] text-[#536471]">
          Notes
        </p>
        {rootNotes.map((note) => (
          <TreeItem
            key={note.id}
            note={note}
            level={0}
            onSelect={onSelectNote}
            activeNoteId={activeNoteId}
            draggedNoteId={draggedNoteId}
            dropTarget={dropTarget}
            onDragStart={setDraggedNoteId}
            onDragEnd={finishDrag}
            onPreviewDrop={previewDropTarget}
            onDropNote={handleDropNote}
            onDragLeave={() => setDropTarget(null)}
          />
        ))}

        {/* Shared — its own section just below the main notes, with a gap. */}
        {sharedNotes.length > 0 && (
          <div className="mt-5">
            <p className="mb-1 flex items-center gap-1.5 px-2 text-[10px] font-black uppercase tracking-[0.22em] text-[#536471]">
              <Users className="h-3 w-3" />
              Shared
            </p>
            {sharedNotes.map((note) => (
              <button
                key={note.id}
                onClick={() => onSelectNote(note.id)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left text-sm transition",
                  activeNoteId === note.id
                    ? "bg-[#ff477e]/15 text-[#ff8fac]"
                    : "text-[#d7dbdc] hover:bg-white/[0.04]"
                )}
                title={`${note.title || "Untitled"} — ${note.myRole === "edit" ? "Can edit" : "View only"}`}
              >
                {note.icon ? (
                  <span className="text-base leading-none">
                    <TwemojiText text={note.icon} />
                  </span>
                ) : (
                  <FileText className="h-4 w-4 shrink-0 text-[#71767b]" />
                )}
                <span className="min-w-0 flex-1 truncate">{note.title || "Untitled"}</span>
                {note.myRole === "view" && (
                  <span className="text-[9px] font-black uppercase tracking-wide text-[#71767b]">view</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-white/[0.06] px-3 py-2">
        <button
          onClick={() => onOpenTrash?.()}
          className="flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-sm font-bold text-[#71767b] transition hover:bg-white/[0.04] hover:text-[#d7dbdc]"
        >
          <Trash2 className="h-4 w-4" />
          Trash
        </button>
      </div>
    </div>
  );
}
